// vg-binding: P7.D-27
//
// apps/vendor/src/features/inventory/StockAdjustmentModal.tsx
//
// Phase 7 Wave 15 polish — vendor stock adjustment modal.
// DECREASE/CORRECTION auto-apply; INCREASE creates a pending admin request.

import { useEffect, useState } from 'react';

import { Button, Input } from '@printway/ui';

import { useTranslation } from '../../lib/i18n/index.js';
import {
  useCreateAdjustment,
  type AdjustmentType,
  type InventoryApiError,
  type StockRow,
} from './api.js';

interface Props {
  open: boolean;
  stock: StockRow | null;
  onClose: () => void;
  onSuccess: (msg: string) => void;
}

export function StockAdjustmentModal({
  open,
  stock,
  onClose,
  onSuccess,
}: Props): JSX.Element | null {
  const { t } = useTranslation('inventory');
  const [type, setType] = useState<AdjustmentType>('DECREASE');
  const [qtyStr, setQtyStr] = useState('');
  const [reason, setReason] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const m = useCreateAdjustment();

  useEffect(() => {
    if (!open) {
      setType('DECREASE');
      setQtyStr('');
      setReason('');
      setErrorMsg(null);
    }
  }, [open]);

  if (!open || !stock) return null;

  const qty = Number.parseInt(qtyStr, 10);
  const qtyValid = Number.isFinite(qty) && qty >= 1;
  const canSubmit =
    qtyValid && reason.trim().length > 0 && !m.isPending;

  async function handleSubmit(): Promise<void> {
    if (!stock) return;
    setErrorMsg(null);
    // UAT #2141 — DECREASE/CORRECTION áp dụng NGAY (không qua duyệt);
    // xác nhận trước khi trừ kho để tránh thao tác nhầm.
    if (type !== 'INCREASE') {
      const ok = window.confirm(
        t('adjust.applyNowConfirm', {
          type: t(type === 'DECREASE' ? 'adjust.typeDecrease' : 'adjust.typeCorrection'),
          qty,
          onHand: stock.on_hand,
        }),
      );
      if (!ok) return;
    }
    try {
      const r = await m.mutateAsync({
        variant_id: stock.variant_id,
        warehouse_id: stock.warehouse_id,
        type,
        quantity: qty,
        reason: reason.trim(),
      });
      const msg = r.applied
        ? t('adjust.successApplied', { onHand: r.on_hand_after ?? t('adjust.unknownValue') })
        : t('adjust.successPending');
      onSuccess(msg);
    } catch (err) {
      const e = err as InventoryApiError;
      setErrorMsg(e.message);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="inventory-adjust-modal"
      /* dark-ok: modal scrim, translucent black reads correctly in both modes */
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-md rounded-xl border border-pw-border bg-pw-bg p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-pw-text">{t('adjust.title')}</h2>
        <p className="mt-1 text-xs text-pw-text-muted">
          {t('adjust.subtitle', {
            variant: stock.variant_id.slice(-6),
            warehouse: stock.warehouse_id.slice(-6),
            onHand: stock.on_hand,
          })}
        </p>

        <div className="mt-4 flex flex-col gap-3">
          <fieldset className="flex flex-col gap-2">
            <legend className="text-sm font-medium text-pw-text">{t('adjust.typeLegend')}</legend>
            <div className="flex flex-col gap-1 text-sm">
              {(['DECREASE', 'CORRECTION', 'INCREASE'] as const).map((adjustType) => (
                <label key={adjustType} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="adjust-type"
                    value={adjustType}
                    checked={type === adjustType}
                    onChange={() => setType(adjustType)}
                    data-testid={`inventory-adjust-type-${adjustType.toLowerCase()}`}
                  />
                  <span className="text-pw-text">
                    {t(
                      adjustType === 'DECREASE'
                        ? 'adjust.typeDecrease'
                        : adjustType === 'CORRECTION'
                          ? 'adjust.typeCorrection'
                          : 'adjust.typeIncrease',
                    )}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium text-pw-text">{t('adjust.qtyLabel')}</span>
            <Input
              type="number"
              min={1}
              step={1}
              data-testid="inventory-adjust-qty-input"
              value={qtyStr}
              onChange={(e) => setQtyStr(e.target.value)}
              className="border-pw-border bg-pw-bg text-pw-text focus:border-pw-primary focus:outline-none focus:ring-1 focus:ring-pw-primary"
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium text-pw-text">{t('adjust.reasonLabel')}</span>
            <textarea
              rows={3}
              maxLength={500}
              data-testid="inventory-adjust-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-sm text-pw-text focus:border-pw-primary focus:outline-none focus:ring-1 focus:ring-pw-primary"
            />
          </label>
        </div>

        {type === 'INCREASE' ? (
          <p
            data-testid="inventory-adjust-increase-notice"
            className="mt-3 rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-700"
          >
            {t('adjust.increaseNotice')}
          </p>
        ) : null}

        {errorMsg ? (
          <p
            data-testid="inventory-adjust-error"
            className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            {errorMsg}
          </p>
        ) : null}

        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            data-testid="inventory-adjust-cancel"
            onClick={onClose}
            className="rounded-md border border-pw-border bg-pw-surface px-4 py-2 text-sm font-medium text-pw-text hover:bg-pw-bg"
          >
            {t('adjust.cancel')}
          </Button>
          <Button
            type="button"
            data-testid="inventory-adjust-submit"
            disabled={!canSubmit}
            onClick={() => void handleSubmit()}
            className="rounded-md bg-pw-primary px-4 py-2 text-sm font-semibold text-white hover:bg-pw-primary-strong disabled:cursor-not-allowed disabled:opacity-50"
          >
            {m.isPending ? t('adjust.submitting') : t('adjust.submit')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default StockAdjustmentModal;
